import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthUser, GoogleAuthData } from '../../entities/auth/types';
import { login as loginApi, signup as signupApi, googleLogin as googleLoginApi, logout as logoutApi, getCurrentUser } from './api';
import { analytics } from '../../shared/analytics';
import { trackSignupConversion } from '../../shared/gtag';
import { getGuestToken, clearGuestSession } from './guest';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  googleLogin: (data: GoogleAuthData) => Promise<void>;
  logout: () => Promise<void>;
  signup: (data: { email: string; password: string; firstName: string; lastName: string; acceptedTerms: boolean; acceptedPrivacyPolicy: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('authUser');
      
      if (token && savedUser) {
        try {
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          // Set user ID in analytics for cross-session tracking
          analytics.setUserId(currentUser.id);
          // Suppress our own test traffic (and restore tracking for everyone
          // else — the opt-out persists in localStorage). See §6 of the docs.
          analytics.setInternalUser(!!currentUser.isInternal);
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUser');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Any guest trip made on this device is claimed into the account
      // server-side; the local guest session is retired on success.
      const response = await loginApi({ email, password, guestToken: getGuestToken() || undefined });
      clearGuestSession();
      setUser(response.user);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('authUser', JSON.stringify(response.user));
      // Identify only — the Login event fires server-side (backend/routes/
      // auth.js); a client-side copy double-counted every login.
      analytics.setUserId(response.user.id);
      analytics.setInternalUser(!!response.user.isInternal);
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (data: GoogleAuthData) => {
    try {
      const response = await googleLoginApi({ ...data, guestToken: getGuestToken() || undefined });
      clearGuestSession();
      setUser(response.user);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('authUser', JSON.stringify(response.user));
      // Track signups client-side; logins are tracked server-side only
      // (backend/routes/auth.js) so each login yields ONE Login event.
      // The Google Ads conversion fires on the same new-account-only
      // condition (never on returning logins).
      if (response.isNewUser) {
        analytics.trackSignupFrontend(response.user.id);
        trackSignupConversion();
      }
      analytics.setUserId(response.user.id);
      analytics.setInternalUser(!!response.user.isInternal);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } finally {
      setUser(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      // Reset analytics user
      analytics.reset();
    }
  };

  const signup = async (data: { email: string; password: string; firstName: string; lastName: string; acceptedTerms: boolean; acceptedPrivacyPolicy: boolean }) => {
    try {
      const response = await signupApi({ ...data, guestToken: getGuestToken() || undefined });
      clearGuestSession();
      setUser(response.user);
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('authUser', JSON.stringify(response.user));
      // Track signup completion in frontend and set user ID in analytics.
      // Also fire the Google Ads conversion: an email/password signup is
      // always a new account.
      analytics.trackSignupFrontend(response.user.id);
      trackSignupConversion();
      analytics.setUserId(response.user.id);
      analytics.setInternalUser(!!response.user.isInternal);
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    googleLogin,
    logout,
    signup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 